import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useAdminViewAs } from '@/contexts/AdminViewAsContext';
import { cacheVerificationStatus, getCachedVerificationStatus } from '@/lib/offlineStore';

interface VerificationStatus {
  isVerified: boolean;
  hasLicense: boolean;
  isPending: boolean;
  /** True if user is an active member of at least one team (trainee bypass) */
  hasTeamAccess: boolean;
  loading: boolean;
}

// Module-level cache — verification status changes infrequently
const verifyCache = new Map<string, { status: Omit<VerificationStatus, 'loading'>; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useVerificationStatus(): VerificationStatus {
  const { user, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<VerificationStatus>({
    isVerified: false,
    hasLicense: false,
    isPending: false,
    hasTeamAccess: false,
    loading: true,
  });
  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setStatus({ isVerified: false, hasLicense: false, isPending: false, hasTeamAccess: false, loading: false });
      return;
    }

    // Return cached result if fresh
    const cached = verifyCache.get(user.id);
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      setStatus({ ...cached.status, loading: false });
      fetchedRef.current = user.id;
      return;
    }

    if (fetchedRef.current === user.id) return;
    fetchedRef.current = user.id;

    const fetch = async () => {
      // If offline, serve from IndexedDB cache
      if (!navigator.onLine) {
        try {
          const cached = await getCachedVerificationStatus(user.id);
          if (cached) {
            setStatus({ ...cached.status, loading: false });
            return;
          }
        } catch { /* fall through */ }
        setStatus({ isVerified: false, hasLicense: false, isPending: false, hasTeamAccess: false, loading: false });
        return;
      }

      const [accountResult, agentResult, teamResult] = await Promise.all([
        supabase
          .from('user_account_levels')
          .select('is_verified, verification_requested_at')
          .eq('user_id', user.id)
          .single(),
        supabase
          .from('agents')
          .select('license_number')
          .eq('user_id', user.id)
          .maybeSingle(),
        supabase
          .from('team_members')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'active')
          .limit(1),
      ]);

      const accountLevel = accountResult.data;
      const agent = agentResult.data;
      const hasLicense = !!agent?.license_number;
      const isVerified = !!accountLevel?.is_verified;
      const isPending = hasLicense && !isVerified && !!accountLevel?.verification_requested_at;
      const hasTeamAccess = (teamResult.data?.length ?? 0) > 0;

      const result = { isVerified, hasLicense, isPending, hasTeamAccess };
      verifyCache.set(user.id, { status: result, ts: Date.now() });
      setStatus({ ...result, loading: false });

      // Write-through: cache to IndexedDB for offline access
      cacheVerificationStatus(user.id, result).catch(() => {});
    };

    fetch();
  }, [isAuthenticated, user]);

  // Admin "View As" override — simulate different user perspectives
  const { overrides } = useAdminViewAs();
  if (overrides) {
    return {
      ...overrides,
      loading: status.loading,
    };
  }

  return status;
}
